import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types/user';
import { preloadForAuthenticatedUser } from '../lib/preload';
import { api } from '../lib/api';
import { AUTH_CONFIG } from '../lib/constants';
import { isOnboardingComplete } from '../lib/onboarding-completion';

interface AuthState {
  // Auth tokens
  token: string | null;
  refreshToken: string | null;

  // User data
  user: User | null;

  // Profile completion status
  isProfileComplete: boolean;

  // Hydration status - true once Zustand has finished loading from localStorage
  hasHydrated: boolean;

  // Actions
  login: (token: string, refreshToken: string | null, user: User) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  setProfileComplete: (isComplete: boolean) => void;
  setToken: (token: string) => void;
  setRefreshToken: (token: string | null) => void;
  setHasHydrated: (hydrated: boolean) => void;

  // Helpers
  isAuthenticated: () => boolean;
  getToken: () => string | null;
}

type PersistedAuthState = Pick<AuthState, 'token' | 'refreshToken' | 'user' | 'isProfileComplete'>;

const DEFAULT_PERSISTED_AUTH_STATE: PersistedAuthState = {
  token: null,
  refreshToken: null,
  user: null,
  isProfileComplete: false,
};

function normalizePersistedAuthState(
  state?: Partial<PersistedAuthState> | null
): PersistedAuthState {
  return {
    token: typeof state?.token === 'string' ? state.token : null,
    refreshToken: typeof state?.refreshToken === 'string' ? state.refreshToken : null,
    user: state?.user ?? null,
    isProfileComplete: Boolean(state?.isProfileComplete),
  };
}

function syncApiClientWithPersistedAuthState(state: PersistedAuthState) {
  if (state.token) {
    api.setAuthToken(state.token);
    api.setRefreshToken(state.refreshToken || state.token);
    return;
  }

  api.clearTokens();
}

function readPersistedAuthState(rawValue: string | null): PersistedAuthState {
  if (!rawValue) {
    return DEFAULT_PERSISTED_AUTH_STATE;
  }

  try {
    const parsed = JSON.parse(rawValue) as {
      state?: Partial<PersistedAuthState>;
    };

    return normalizePersistedAuthState(parsed.state);
  } catch {
    return DEFAULT_PERSISTED_AUTH_STATE;
  }
}

/**
 * Sync api client auth state from the persisted Zustand auth.
 *
 * Used by cross-tab storage listeners so other tabs can adopt the latest
 * token, refresh token, user, and profile completion state without echoing
 * the incoming storage event back into localStorage (hence the fake/empty storage object).
 */
export function syncAuthStateFromStorageValue(rawValue: string | null) {
  const persistedState = readPersistedAuthState(rawValue);
  const originalStorage = useAuthStore.persist.getOptions().storage;

  useAuthStore.persist.setOptions({
    storage: {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    },
  });

  try {
    useAuthStore.setState(state => ({
      ...state,
      ...persistedState,
    }));

    syncApiClientWithPersistedAuthState(persistedState);
  } finally {
    useAuthStore.persist.setOptions({ storage: originalStorage });
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      refreshToken: null,
      user: null,
      isProfileComplete: false,
      hasHydrated: false,

      // Login action
      login: (token, refreshToken, user) => {
        set({
          token,
          refreshToken,
          user,
          isProfileComplete: isOnboardingComplete(user),
        });

        // Sync tokens to API client
        api.setAuthToken(token);
        if (refreshToken) {
          api.setRefreshToken(refreshToken);
        }

        // Preload dashboard components for better UX
        // We'll check profile completion status separately
        preloadForAuthenticatedUser();
      },

      // Logout action
      logout: () => {
        set({
          token: null,
          refreshToken: null,
          user: null,
          isProfileComplete: false,
        });

        // Clear tokens from API client
        api.clearTokens();
      },

      // Update user data
      updateUser: userData =>
        set(state => ({
          user: state.user ? { ...state.user, ...userData } : null,
          isProfileComplete: state.user
            ? isOnboardingComplete({ ...state.user, ...userData })
            : false,
        })),

      // Set profile completion status
      setProfileComplete: isComplete => set({ isProfileComplete: isComplete }),

      // Update token (for refresh)
      setToken: token => {
        set({ token });
        // Sync to API client
        api.setAuthToken(token);
      },

      // Update refresh token (used after silent refresh)
      setRefreshToken: token => {
        set({ refreshToken: token });
        api.setRefreshToken(token);
      },

      // Set hydration status
      setHasHydrated: hydrated => set({ hasHydrated: hydrated }),

      // Check if user is authenticated
      isAuthenticated: () => {
        const state = get();
        return !!state.token && !!state.user;
      },

      // Get current token
      getToken: () => get().token,
    }),
    {
      name: AUTH_CONFIG.STORAGE_KEY,
      // Only persist essential auth data (NOT hasHydrated - that's runtime state)
      partialize: state => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isProfileComplete: state.isProfileComplete,
      }),
      // Sync tokens to API client after hydration completes
      onRehydrateStorage: () => {
        return (state, error) => {
          if (!error) {
            syncApiClientWithPersistedAuthState(normalizePersistedAuthState(state));
          }

          // Mark hydration as complete
          useAuthStore.setState({ hasHydrated: true });
        };
      },
    }
  )
);
